import { useState, useEffect } from "react";
import { X, Sparkles } from "lucide-react";
import CategoriesModal from "./CategoriesModal.jsx";
import { resolveIcon } from "../../constants.js";

export default function DuplicateModal({ open, onClose, currentTitle, currentCategories, legs, onSubmit, busy }) {
  const [title, setTitle] = useState("");
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [editCatsOpen, setEditCatsOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setTitle(currentTitle ? `${currentTitle} (copy)` : "");
      setCategories(JSON.parse(JSON.stringify(currentCategories || [])));
      setSelected(new Set((legs || []).map((l) => l.id)));
      setError(null);
    }
  }, [open, currentTitle, currentCategories, legs]);

  if (!open) return null;

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOn = (legs || []).length > 0 && (legs || []).every((l) => selected.has(l.id));
  const toggleAll = () => {
    if (allOn) setSelected(new Set());
    else setSelected(new Set((legs || []).map((l) => l.id)));
  };

  const handleSubmit = () => {
    setError(null);
    const trimmed = title.trim();
    if (!trimmed) { setError("Title is required"); return; }
    const legIds = (legs || []).map((l) => l.id).filter((id) => selected.has(id));
    if (legIds.length === 0) { setError("Pick at least one leg to duplicate"); return; }
    onSubmit({ title: trimmed, categories, legs: legIds });
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
        <div
          className="w-full sm:max-w-lg bg-white dark:bg-slate-800 rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: "92dvh" }}
        >
          <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "'Fraunces', Georgia, serif" }}>
                Duplicate trip
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Creates a fresh trip with new ids</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1.5 block">New title</label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 focus:border-slate-900 dark:focus:border-slate-400 focus:outline-none text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-700"
                placeholder="Trip title"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Categories</label>
                <button
                  type="button"
                  onClick={() => setEditCatsOpen(true)}
                  className="text-xs text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 font-medium underline-offset-4 hover:underline"
                >
                  Edit
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {categories.map((c) => {
                  const Icon = resolveIcon(c.icon);
                  return (
                    <span
                      key={c.id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-medium"
                      style={{ background: `${c.colour}10`, color: c.colour, borderColor: `${c.colour}40` }}
                    >
                      <Icon size={11} />
                      {c.label}
                    </span>
                  );
                })}
                {categories.length === 0 && (
                  <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                    <Sparkles size={12} /> No categories — click Edit to add some
                  </span>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">Legs to copy</label>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-xs text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 font-medium underline-offset-4 hover:underline"
                >
                  {allOn ? "Deselect all" : "Select all"}
                </button>
              </div>
              <div className="space-y-1.5">
                {(legs || []).map((leg) => {
                  const on = selected.has(leg.id);
                  return (
                    <label
                      key={leg.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-xl border bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 cursor-pointer transition-colors"
                      style={{ borderColor: on ? leg.colour : "#e2e8f0" }}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(leg.id)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="w-2 h-6 rounded-full" style={{ background: leg.colour }} />
                      <span className="text-lg">{leg.emoji}</span>
                      <div className="flex-1 min-w-0 text-sm">
                        <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{leg.city}, {leg.country}</div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">{leg.start} → {leg.end}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700">{error}</div>
            )}
          </div>

          <div className="px-5 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-100 dark:border-slate-700 flex gap-2 justify-end flex-shrink-0">
            <button
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 rounded-full text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={busy}
              className="px-5 py-2 rounded-full bg-slate-900 dark:bg-slate-600 text-white font-medium hover:bg-slate-700 dark:hover:bg-slate-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? "Duplicating…" : "Duplicate"}
            </button>
          </div>
        </div>
      </div>

      <CategoriesModal
        open={editCatsOpen}
        categories={categories}
        items={{}}
        onClose={() => setEditCatsOpen(false)}
        onSave={(next) => setCategories(next)}
      />
    </>
  );
}
